import React, { useState, useRef } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell
} from '@material-ui/core'
import axios from 'axios'
import { tokens } from '../../../styles/desertFloraTheme'

const inputBase = {
  background: 'transparent',
  border: '1px solid transparent',
  padding: '6px 8px',
  borderRadius: 6,
  fontSize: 13,
  color: tokens.ink,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color .12s, box-shadow .12s, background .12s'
}

const inputFocusStyle = {
  borderColor: tokens.primary,
  boxShadow: '0 0 0 2px rgba(31,94,58,.14)',
  background: tokens.surface
}

const headerCellStyle = {
  fontFamily: tokens.fontMono,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  background: tokens.surface2,
  padding: '8px 12px',
  borderBottom: '1px solid ' + tokens.line
}

const cellStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid ' + tokens.line
}

const InlineInput = ({ defaultValue, onCommit, style, placeholder, type }) => {
  const [focused, setFocused] = useState(false)

  const mergedStyle = {
    ...inputBase,
    ...style,
    ...(focused ? inputFocusStyle : {})
  }

  return (
    <input
      type={type || 'text'}
      defaultValue={defaultValue}
      placeholder={placeholder}
      style={mergedStyle}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false)
        if (onCommit) onCommit(e.target.value)
      }}
    />
  )
}

const AreaBreakdownTable = ({ projectService, url, onUpdate, areaColumnLabel = 'Area' }) => {
  const [newArea, setNewArea] = useState({ label: '', quantity: '', unit: projectService.unit || '', notes: '' })
  const [addFocused, setAddFocused] = useState({})
  const debounceRef = useRef(null)

  const handleAreaFieldChange = (areaId, field, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}/areas/${areaId}`, { [field]: value })
        onUpdate()
      } catch (e) {
        console.error('Area update failed:', e)
      }
    }, 300)
  }

  const handleAddArea = async () => {
    if (!newArea.label || !newArea.quantity) return
    try {
      await axios.post(`${url}/api/bid-estimation/project-services/${projectService._id}/add-area`, {
        ...newArea,
        quantity: Number(newArea.quantity)
      })
      setNewArea({ label: '', quantity: '', unit: projectService.unit || '', notes: '' })
      onUpdate()
    } catch (e) {
      console.error('Add area failed:', e)
    }
  }

  const handleDeleteArea = async (areaId) => {
    try {
      await axios.delete(`${url}/api/bid-estimation/project-services/${projectService._id}/areas/${areaId}`)
      onUpdate()
    } catch (e) {
      console.error('Delete area failed:', e)
    }
  }

  const areas = projectService.areas || []
  const totalQty = areas.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0)

  const [deleteHover, setDeleteHover] = useState(null)

  return (
    <div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={headerCellStyle}>{areaColumnLabel}</TableCell>
            <TableCell style={headerCellStyle}>Building</TableCell>
            <TableCell align="right" style={headerCellStyle}>Qty</TableCell>
            <TableCell style={headerCellStyle}>Unit</TableCell>
            <TableCell style={headerCellStyle}>Notes</TableCell>
            <TableCell style={{ ...headerCellStyle, width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {areas.map(area => (
            <TableRow key={area._id}>
              <TableCell style={cellStyle}>
                <InlineInput
                  defaultValue={area.label || ''}
                  style={{ fontWeight: 500 }}
                  onCommit={(val) => handleAreaFieldChange(area._id, 'label', val)}
                />
              </TableCell>
              <TableCell style={cellStyle}>
                <InlineInput
                  defaultValue={area.building || ''}
                  onCommit={(val) => handleAreaFieldChange(area._id, 'building', val)}
                />
              </TableCell>
              <TableCell align="right" style={cellStyle}>
                <InlineInput
                  defaultValue={area.quantity || ''}
                  type="number"
                  style={{
                    fontFamily: tokens.fontMono,
                    fontWeight: 600,
                    textAlign: 'right',
                    width: 80
                  }}
                  onCommit={(val) => handleAreaFieldChange(area._id, 'quantity', Number(val))}
                />
              </TableCell>
              <TableCell style={cellStyle}>
                <InlineInput
                  defaultValue={area.unit || ''}
                  onCommit={(val) => handleAreaFieldChange(area._id, 'unit', val)}
                />
              </TableCell>
              <TableCell style={cellStyle}>
                <InlineInput
                  defaultValue={area.notes || ''}
                  style={{ fontSize: 12, color: tokens.ink3, fontStyle: 'italic' }}
                  onCommit={(val) => handleAreaFieldChange(area._id, 'notes', val)}
                />
              </TableCell>
              <TableCell style={{ ...cellStyle, width: 40, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => handleDeleteArea(area._id)}
                  onMouseEnter={() => setDeleteHover(area._id)}
                  onMouseLeave={() => setDeleteHover(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    fontSize: 16,
                    lineHeight: 1,
                    color: deleteHover === area._id ? tokens.danger : tokens.ink4,
                    transition: 'color .12s'
                  }}
                  title="Delete area"
                >
                  &times;
                </button>
              </TableCell>
            </TableRow>
          ))}

          {/* Totals row */}
          {areas.length > 0 && (
            <TableRow>
              <TableCell style={{ ...cellStyle, background: tokens.surface2, borderBottom: 'none' }} />
              <TableCell style={{ ...cellStyle, background: tokens.surface2, borderBottom: 'none' }} />
              <TableCell align="right" style={{
                ...cellStyle,
                background: tokens.surface2,
                fontFamily: tokens.fontMono,
                fontWeight: 600,
                fontSize: 13,
                borderBottom: 'none'
              }}>
                {totalQty.toLocaleString('en-US')}
              </TableCell>
              <TableCell style={{ ...cellStyle, background: tokens.surface2, borderBottom: 'none' }} />
              <TableCell style={{ ...cellStyle, background: tokens.surface2, borderBottom: 'none' }} />
              <TableCell style={{ ...cellStyle, background: tokens.surface2, width: 40, borderBottom: 'none' }} />
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Add area footer */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid ' + tokens.line
      }}>
        <span
          role="button"
          tabIndex={0}
          onClick={handleAddArea}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea() }}
          style={{
            color: tokens.primary,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
        >
          + Add area
        </span>

        {/* Inline inputs for new area (shown when typing) */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginTop: 8,
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="Area name"
            value={newArea.label}
            onChange={(e) => setNewArea(prev => ({ ...prev, label: e.target.value }))}
            onFocus={() => setAddFocused(prev => ({ ...prev, label: true }))}
            onBlur={() => setAddFocused(prev => ({ ...prev, label: false }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea() }}
            style={{
              ...inputBase,
              flex: 1,
              ...(addFocused.label ? inputFocusStyle : { borderColor: tokens.line })
            }}
          />
          <input
            type="number"
            placeholder="Qty"
            value={newArea.quantity}
            onChange={(e) => setNewArea(prev => ({ ...prev, quantity: e.target.value }))}
            onFocus={() => setAddFocused(prev => ({ ...prev, qty: true }))}
            onBlur={() => setAddFocused(prev => ({ ...prev, qty: false }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea() }}
            style={{
              ...inputBase,
              width: 80,
              fontFamily: tokens.fontMono,
              fontWeight: 600,
              textAlign: 'right',
              ...(addFocused.qty ? inputFocusStyle : { borderColor: tokens.line })
            }}
          />
          <input
            type="text"
            placeholder="Unit"
            value={newArea.unit}
            onChange={(e) => setNewArea(prev => ({ ...prev, unit: e.target.value }))}
            onFocus={() => setAddFocused(prev => ({ ...prev, unit: true }))}
            onBlur={() => setAddFocused(prev => ({ ...prev, unit: false }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea() }}
            style={{
              ...inputBase,
              width: 70,
              ...(addFocused.unit ? inputFocusStyle : { borderColor: tokens.line })
            }}
          />
          <input
            type="text"
            placeholder="Notes"
            value={newArea.notes}
            onChange={(e) => setNewArea(prev => ({ ...prev, notes: e.target.value }))}
            onFocus={() => setAddFocused(prev => ({ ...prev, notes: true }))}
            onBlur={() => setAddFocused(prev => ({ ...prev, notes: false }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea() }}
            style={{
              ...inputBase,
              flex: 1,
              fontSize: 12,
              ...(addFocused.notes ? inputFocusStyle : { borderColor: tokens.line })
            }}
          />
        </div>

        <div style={{ fontSize: 11, color: tokens.ink4, marginTop: 6 }}>
          use to split by building, zone, or phase
        </div>
      </div>
    </div>
  )
}

export default AreaBreakdownTable
